/**
 * Worauf sich eine Notiz bezieht — ein Suchfeld über Kunden, Projekte,
 * Lieferanten und offene Leeds zugleich. Eine Notiz darf mehrere Bezüge
 * tragen (Kunde UND Lieferant: „Teichfolie für Müller bei X anfragen"); je Art
 * gilt einer, eine neue Wahl ersetzt den alten.
 */
import { useMemo } from "react";

import { useLieferanten } from "../../hooks/useBelege";
import { useKunden, useProjekte } from "../../hooks/usePlanung";
import { useLeeds } from "../../hooks/usePost";
import type { NotizBezugTeil } from "../../lib/notizen";
import Combobox, { type ComboboxEintrag } from "../Combobox";

const ART: Record<string, { feld: keyof NotizBezugTeil; name: string }> = {
  k: { feld: "kunde_id", name: "Kunde" },
  p: { feld: "projekt_id", name: "Projekt" },
  l: { feld: "lieferant_firma_id", name: "Lieferant" },
  a: { feld: "anfrage_id", name: "Leed" },
};

type Props = { wert: NotizBezugTeil; onWert: (b: NotizBezugTeil) => void };

export default function BezugWahl({ wert, onWert }: Props) {
  const kunden = useKunden();
  const projekte = useProjekte(true);
  const lieferanten = useLieferanten();
  const leeds = useLeeds("offen");

  const eintraege = useMemo<ComboboxEintrag[]>(() => [
    ...(kunden.data || []).map((k) => ({ id: "k:" + k.id, label: k.name, zusatz: "Kunde" })),
    ...(projekte.data || []).map((p) => ({ id: "p:" + p.id, label: p.title, zusatz: `Projekt · ${p.kunde_name || ""}`.replace(/ · $/, "") })),
    ...(lieferanten.data || []).map((l) => ({ id: "l:" + l.id, label: l.name, zusatz: "Lieferant" })),
    ...(leeds.data?.leeds || []).map((a) => ({ id: "a:" + a.id, label: a.name || a.betreff, zusatz: "Leed" })),
  ], [kunden.data, projekte.data, lieferanten.data, leeds.data]);

  const label = (id: string) => eintraege.find((e) => e.id === id)?.label || "…";
  const gewaehlt = Object.entries(ART)
    .map(([kurz, a]) => ({ kurz, ...a, id: wert[a.feld] || "" }))
    .filter((g) => g.id);

  const waehlen = (roh: string) => {
    const [kurz, ...rest] = roh.split(":");
    const art = ART[kurz];
    if (!art) return;
    onWert({ ...wert, [art.feld]: rest.join(":") });
  };

  return (
    <div className="nz-bezug">
      {gewaehlt.map((g) => (
        <span key={g.feld} className="nz-bezug-chip">
          <small>{g.name}</small> {label(`${g.kurz}:${g.id}`)}
          <button type="button" aria-label={`${g.name} entfernen`} onClick={() => onWert({ ...wert, [g.feld]: "" })}>×</button>
        </span>
      ))}
      <div className="nz-bezug-suche">
        <Combobox
          wert=""
          eintraege={eintraege}
          onWaehlen={waehlen}
          platzhalter={gewaehlt.length ? "Weiteren Bezug …" : "Kunde, Projekt, Lieferant oder Leed …"}
          ariaLabel="Bezug der Notiz suchen"
        />
      </div>
    </div>
  );
}
